import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  IconButton,
  Chip,
  Box,
  Link,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import CardHeadMenu from "../../Components/Card/CardHeadMenu/CardHeadMenu";
import IOSSwitch from "../../Components/Buttons/IOSSwitch";
import Swal from "sweetalert2";
import configServ from "../../services/config";
import { useSelector } from "react-redux";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import YouTubeIcon from "@mui/icons-material/YouTube";

const platformIcons = {
  Facebook: <FacebookIcon sx={{ color: "#1877F2" }} />,
  Instagram: <InstagramIcon sx={{ color: "#E4405F" }} />,
  Twitter: <TwitterIcon sx={{ color: "#1DA1F2" }} />,
  LinkedIn: <LinkedInIcon sx={{ color: "#0077B5" }} />,
  YouTube: <YouTubeIcon sx={{ color: "#FF0000" }} />,
};
const SocialMediaConfiguration = () => {
  const [isCreate, setIsCreate] = useState(false);
  const [isEdit, setIsEdit] = useState(false); // edit mode flag
  const { company_id } = useSelector((state) => state.GlobalVariables);
  const [socialMediaList, setSocialMediaList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    platform: "",
    url: "",
    is_active: true,
  });

  // 🔹 Separate function to fetch social media configs
  const fetchSocialMedia = async () => {
    try {
      setLoading(true);
      const res = await configServ.getSocialMediaByCompany({
        company_id: company_id,
      });
      if (res.success) {
        setSocialMediaList(res.data);
      } else {
        console.error("Failed to fetch social media:", res.data.message);
      }
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 useEffect only calls the function
  useEffect(() => {
    if (company_id) {
      fetchSocialMedia(company_id);
    }
  }, [company_id]);

  const handlePopup = () => {
    setIsCreate(!isCreate);
    setIsEdit(false); // reset edit state when opening
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const clear = () => {
    setFormData({ platform: "", url: "", is_active: true });
    setIsEdit(false);
  };

  const handleSubmit = async () => {
    try {
      let res;

      if (isEdit) {
        res = await configServ.updateSocialMedia(formData.id, formData);
      } else {
        // Create request (POST)
        res = await configServ.storeSocialMedia({
          ...formData,
          company_id: company_id,
        });
      }

      if (res.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text:
            res.message ||
            (isEdit
              ? "Social media configuration updated successfully!"
              : "Social media configuration created successfully!"),
          timer: 2000,
          showConfirmButton: false,
        });

        // Optional: reset form or reload list
        clear();
        fetchSocialMedia();
        setIsCreate(false);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.error || "Something went wrong!",
      });
      console.error("Error saving social media configuration:", error);
    }
  };

  // Example function to load edit data
  const loadEditData = (data) => {
    setFormData(data);
    setIsEdit(true);
    setIsCreate(true);
  };

  const onEdit = (item) => {
    console.log("onEdit=", item);
    setIsCreate(true);
    setIsEdit(true);

    setFormData({
      id: item.id, // ✅ Include ID so update API works
      company_id: item.company_id, // optional, if needed for update
      platform: item.platform || "",
      url: item.url || "",
      is_active: item.is_active === 1, // convert to boolean
    });
  };

  return (
    <>
      <CardHeadMenu
        prefix={"Na"}
        suffix={"Na"}
        title={"Social Media Configurations"}
        handlePopup={handlePopup}
        create={isCreate}
        isEdit={isEdit}
      />

      {isCreate && (
        <motion.div
          style={{
            width: "100%",
            backgroundColor: "#e3f3fc",
            padding: 20,
            borderRadius: "10px",
          }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.1, delay: 0.1 }}
        >
          <Grid container spacing={2}>
            {/* Social Media Name Dropdown */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Social Media</InputLabel>
                <Select
                  name="platform"
                  value={formData.platform}
                  onChange={handleChange}
                  sx={{ backgroundColor: "white" }}
                >
                  <MenuItem value="Facebook">Facebook</MenuItem>
                  <MenuItem value="Instagram">Instagram</MenuItem>
                  <MenuItem value="Twitter">Twitter</MenuItem>
                  <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                  <MenuItem value="YouTube">YouTube</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* URL Field */}
            <Grid item xs={12} md={5}>
              <TextField
                name="url"
                label="Profile URL"
                sx={{ backgroundColor: "white" }}
                variant="outlined"
                size="small"
                fullWidth
                value={formData.url}
                onChange={handleChange}
              />
            </Grid>

            {/* Active Status */}
            <Grid item xs={12} md={3}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <IOSSwitch
                      checked={formData.is_active}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          is_active: e.target.checked,
                        }))
                      }
                      name="is_active"
                    />
                  }
                  label="Active"
                />
              </FormGroup>
            </Grid>
          </Grid>

          {/* Buttons */}
          <Grid container spacing={2} justifyContent="flex-end" mt={2}>
            <Grid item>
              <Button
                onClick={clear}
                variant="contained"
                color="secondary"
                startIcon={<RotateLeftIcon />}
              >
                Reset
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color={isEdit ? "success" : "primary"}
                startIcon={isEdit ? <UpdateIcon /> : <SaveIcon />}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Grid>
          </Grid>
        </motion.div>
      )}
      <Box sx={{ flexGrow: 1, p: 2 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="50vh"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {socialMediaList.map((item) => (
              <Grid key={item.id} item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    boxShadow: 3,
                    position: "relative",
                  }}
                >
                  {/* Edit Button */}
                  <IconButton
                    size="small"
                    sx={{ position: "absolute", top: 8, right: 8 }}
                    onClick={() => onEdit(item)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>

                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {/* Platform Icon */}
                      <Avatar sx={{ bgcolor: "transparent" }}>
                        {platformIcons[item.platform] || null}
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold">
                        {item.platform}
                      </Typography>
                    </Box>

                    {/* Link */}
                    <Link
                      href={item.url}
                      target="_blank"
                      rel="noopener"
                      sx={{
                        wordBreak: "break-all",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {item.url}
                      <IconButton size="small" sx={{ ml: 0.5 }}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Link>

                    {/* Status */}
                    <Chip
                      label={item.is_active ? "Active" : "Inactive"}
                      color={item.is_active ? "success" : "default"}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </>
  );
};

export default SocialMediaConfiguration;
